import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiFileText, FiDownload, FiPrinter } from 'react-icons/fi';
import api from '../../services/api';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';

const CustomerStatement = () => {
  const [party, setParty] = useState('');
  const [asOf, setAsOf] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const handleGenerate = async () => {
    if (!party.trim()) {
      toast.info('Please select a customer');
      return;
    }
    setLoading(true);
    setPdfUrl(null);
    try {
      const params = new URLSearchParams({ party, as_of: asOf });
      const response = await api.get(`/pdf/statement?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch (error) {
      toast.error('Failed to generate statement');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    const safeName = party.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    link.download = `Statement_${safeName}_${asOf}.pdf`;
    link.click();
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const win = window.open(pdfUrl, '_blank');
    if (win) {
      win.addEventListener('load', () => {
        win.print();
      });
    }
  };

  const filterLabel = { width: '140px', fontSize: '13px', fontWeight: 600, flexShrink: 0 };
  const filterRow = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' };

  return (
    <div className="card" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ background: '#5f9ea0', color: '#fff', padding: '10px 16px', borderRadius: '4px', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Customer Statement</h2>
      </div>

      {/* Filters */}
      <div style={{ maxWidth: '600px', marginBottom: '20px' }}>
        <div style={filterRow}>
          <span style={filterLabel}>*Customer</span>
          <div style={{ flex: 1 }}>
            <TradePartnerAutocomplete
              value={party}
              onChange={(name, tp) => setParty(tp ? tp.name : name)}
              tpTypes="Customer,Shipper,Consignee"
              placeholder="Search customer..."
            />
          </div>
        </div>
        <div style={filterRow}>
          <span style={filterLabel}>*As of Date</span>
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          />
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={handleGenerate}
          disabled={loading}
          style={{
            padding: '8px 24px', backgroundColor: '#3b82f6', color: '#fff',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '6px', opacity: loading ? 0.6 : 1,
          }}
        >
          <FiFileText /> {loading ? 'Generating...' : 'Generate Statement'}
        </button>
        {pdfUrl && (
          <>
            <button
              onClick={handleDownload}
              style={{
                padding: '8px 16px', backgroundColor: '#10b981', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <FiDownload /> Download
            </button>
            <button
              onClick={handlePrint}
              style={{
                padding: '8px 16px', backgroundColor: '#6366f1', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <FiPrinter /> Print
            </button>
          </>
        )}
      </div>

      {/* PDF Preview */}
      {pdfUrl && (
        <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
          <iframe
            src={pdfUrl}
            title="Statement Preview"
            style={{ width: '100%', height: '700px', border: 'none' }}
          />
        </div>
      )}

      {!pdfUrl && !loading && (
        <div className="empty-state">
          <div className="empty-state-icon"><FiFileText size={32} /></div>
          <div className="empty-state-title">Generate a Statement</div>
          <div className="empty-state-text">Select a customer and click "Generate Statement" to view all open invoices</div>
        </div>
      )}
    </div>
  );
};

export default CustomerStatement;
